import { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getLocalDateString } from '../utils/dateUtils';
import { useAuth } from './useAuth';

// Get the week start for a given meeting day
// Week runs from meetingDay to meetingDay-1 (e.g., Tuesday meeting = Tue-Mon week)
function getWeekStartForMeetingDay(meetingDay = 2) {
  const now = new Date();
  const currentDay = now.getDay(); // 0=Sun, 1=Mon, 2=Tue, etc.

  // Calculate days since the last occurrence of meetingDay
  let daysSince;
  if (currentDay >= meetingDay) {
    daysSince = currentDay - meetingDay;
  } else {
    daysSince = currentDay + (7 - meetingDay);
  }

  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - daysSince);
  weekStart.setHours(0, 0, 0, 0);

  return getLocalDateString(weekStart);
}

// Legacy function for backwards compatibility (defaults to Tuesday)
function getCurrentWeekStart() {
  return getWeekStartForMeetingDay(2);
}

// Format week display showing the range Tue - Mon
function formatWeekDisplay(weekStart) {
  const startDate = new Date(weekStart + 'T00:00:00');
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6); // Monday (6 days after Tuesday)

  const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return `${startStr} - ${endStr}`;
}

// Main hook - now accepts an optional relationship object
// If relationship is provided, filters sessions by relationshipId and uses relationship's meetingDay
export function useOneOnOnes(relationship = null) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const relationshipId = relationship?.id;
  const meetingDay = relationship?.meetingDay ?? 2; // Default to Tuesday
  const participants = relationship?.participants || (user?.uid ? [user.uid] : []);

  // Ref to prevent concurrent session creation in async onSnapshot callback
  const isCreatingSessionRef = useRef(false);

  useEffect(() => {
    if (!user?.uid) {
      setSessions([]);
      setCurrentSession(null);
      setLoading(false);
      return;
    }

    // Build query - filter by participants and optionally by relationshipId
    let q;
    if (relationshipId) {
      q = query(
        collection(db, 'oneOnOnes'),
        where('relationshipId', '==', relationshipId),
        where('participants', 'array-contains', user.uid),
        orderBy('weekStart', 'desc')
      );
    } else {
      // Query sessions where user is a participant (multi-tenant)
      q = query(
        collection(db, 'oneOnOnes'),
        where('participants', 'array-contains', user.uid),
        orderBy('weekStart', 'desc')
      );
    }

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        let sessionsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));

        // If no relationshipId provided, filter to only sessions without relationshipId (legacy)
        if (!relationshipId) {
          sessionsData = sessionsData.filter(s => !s.relationshipId);
        }

        setSessions(sessionsData);

        // Find the active session (status === 'active') - sessions persist until completed
        // No auto-creation based on week - sessions only created on completion of previous
        const activeSession = sessionsData.find(s => s.status === 'active');

        if (activeSession) {
          setCurrentSession(activeSession);
        } else {
          // No active session - user will need to start a new one
          setCurrentSession(null);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Error fetching sessions:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [relationshipId, meetingDay, user?.uid, participants.join(',')]);

  // Complete current session and auto-create next one
  const completeSession = async (sessionId) => {
    // Mark current session as completed
    await updateDoc(doc(db, 'oneOnOnes', sessionId), {
      status: 'completed',
      completedAt: new Date()
    });

    // Auto-create next session
    const newWeekStart = getWeekStartForMeetingDay(meetingDay);
    const newSession = await addDoc(collection(db, 'oneOnOnes'), {
      weekStart: newWeekStart,
      status: 'active',
      participants: participants,
      relationshipId: relationshipId || null,
      createdAt: new Date()
    });

    return newSession.id;
  };

  // Create first session for a new relationship (or when no active session exists)
  const createFirstSession = async () => {
    if (!user?.uid) return null;
    if (isCreatingSessionRef.current) return null;

    isCreatingSessionRef.current = true;
    try {
      const newWeekStart = getWeekStartForMeetingDay(meetingDay);
      const newSession = await addDoc(collection(db, 'oneOnOnes'), {
        weekStart: newWeekStart,
        status: 'active',
        participants: participants,
        relationshipId: relationshipId || null,
        createdAt: new Date()
      });
      return newSession.id;
    } finally {
      isCreatingSessionRef.current = false;
    }
  };

  const uncompleteSession = async (sessionId) => {
    await updateDoc(doc(db, 'oneOnOnes', sessionId), {
      status: 'active',
      completedAt: null
    });
  };

  // Check if a session is editable (current week OR past but not completed)
  const isSessionEditable = (session) => {
    if (!session) return false;
    if (session.id === currentSession?.id) return true; // Current week always editable
    return session.status !== 'completed'; // Past weeks editable until marked complete
  };

  const pastSessions = sessions.filter(s => s.id !== currentSession?.id);

  // One-time cleanup function to remove duplicate weeks caused by timezone bug
  const cleanupDuplicateWeeks = async () => {
    if (!user?.uid) return [];

    const q = query(
      collection(db, 'oneOnOnes'),
      where('participants', 'array-contains', user.uid)
    );
    const snapshot = await getDocs(q);
    const allSessions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    // Group sessions by the Tuesday they should belong to
    const sessionsByTuesday = {};

    allSessions.forEach(session => {
      const date = new Date(session.weekStart + 'T00:00:00');
      const dayOfWeek = date.getDay();

      // Calculate the correct Tuesday for this date
      let daysToTuesday;
      if (dayOfWeek >= 2) {
        daysToTuesday = dayOfWeek - 2;
      } else {
        daysToTuesday = dayOfWeek + 5;
      }

      const tuesday = new Date(date);
      tuesday.setDate(date.getDate() - daysToTuesday);
      const tuesdayStr = `${tuesday.getFullYear()}-${String(tuesday.getMonth() + 1).padStart(2, '0')}-${String(tuesday.getDate()).padStart(2, '0')}`;

      if (!sessionsByTuesday[tuesdayStr]) {
        sessionsByTuesday[tuesdayStr] = [];
      }
      sessionsByTuesday[tuesdayStr].push(session);
    });

    // For each group with duplicates, delete the ones with wrong weekStart
    const deleted = [];
    for (const [tuesdayStr, group] of Object.entries(sessionsByTuesday)) {
      if (group.length > 1) {
        // Keep the one with correct weekStart (tuesdayStr), delete others
        const toKeep = group.find(s => s.weekStart === tuesdayStr);
        const toDelete = group.filter(s => s.weekStart !== tuesdayStr);

        for (const session of toDelete) {
          await deleteDoc(doc(db, 'oneOnOnes', session.id));
          deleted.push(session.weekStart);
        }
      }
    }

    console.log('Cleaned up duplicate weeks:', deleted);
    return deleted;
  };

  return {
    sessions,
    currentSession,
    pastSessions,
    loading,
    error,
    completeSession,
    uncompleteSession,
    createFirstSession,
    isSessionEditable,
    formatWeekDisplay,
    getCurrentWeekStart,
    getWeekStartForMeetingDay,
    cleanupDuplicateWeeks
  };
}

// Export for use elsewhere
export { getWeekStartForMeetingDay, getCurrentWeekStart, formatWeekDisplay };
